import React, {FC, useMemo} from "react";
import {Alert, Button, Form, Input, Select, Space, Table, Typography} from "antd";
import type {TableColumnsType} from "antd";
import {useTranslation} from "react-i18next";
import {FieldTile, StatusTag} from "../../../Components/ui";
import {formatLocalDate} from "../adminDateFormat";
import {EMPTY_OPERATIONS_VALUE, operationalRoomReservationStatusTone} from "../participantOperationsHelpers";
import {dateSorter, numberSorter, sortText, textSorter} from "../tableSorters";
import {displayValue, toPositiveInt} from "./participantDetailHelpers";
import type {ParticipantRoomReservation} from "./participantDetailTypes";
import {DetailPanel, EnumTag, FIELD_GRID_STYLE, useEmptyText} from "./participantDetailUi";
import {ParticipantFormField} from "./ParticipantFormField";
import type {ParticipantDetailModel} from "./useParticipantDetail";

// Hotel room for the primary registration, chosen from the event's room allotments.
export const RoomingSection: FC<{model: ParticipantDetailModel}> = ({model}) => {
    const {t} = useTranslation();
    const emptyText = useEmptyText();
    const {primaryRegistration, primaryRoomReservation, roomReservations, rooming, pending, actions, forms} = model;
    const selectionDisabled = !primaryRegistration || rooming.loading || rooming.failed;
    const roomChoiceLabel = t("room-reservation-choice", {defaultValue: "Hotel / room"});
    const arrivalLabel = t("arrival", {defaultValue: "Arrival"});
    const departureLabel = t("departure", {defaultValue: "Departure"});
    const roomCountLabel = t("room-count", {defaultValue: "Room count"});

    const reservationColumns = useMemo<TableColumnsType<ParticipantRoomReservation>>(() => [
        {
            key: "room",
            title: t("room", {defaultValue: "Room"}),
            sorter: textSorter(reservation => sortText(reservation.hotelName, reservation.roomTypeName)),
            render: (_, reservation) => (
                <Space direction="vertical" size={0}>
                    <Typography.Text>{displayValue(reservation.hotelName)}</Typography.Text>
                    <Typography.Text type="secondary">{displayValue(reservation.roomTypeName)}</Typography.Text>
                </Space>
            )
        },
        {
            key: "dates",
            title: t("dates", {defaultValue: "Dates"}),
            sorter: dateSorter(reservation => reservation.arrivalDate),
            render: (_, reservation) => `${formatLocalDate(reservation.arrivalDate, EMPTY_OPERATIONS_VALUE)} - ${formatLocalDate(reservation.departureDate, EMPTY_OPERATIONS_VALUE)}`
        },
        {
            key: "rooms",
            title: t("rooms", {defaultValue: "Rooms"}),
            sorter: numberSorter(reservation => reservation.roomCount),
            render: (_, reservation) => `${reservation.roomCount} / ${reservation.nightCount} ${t("nights", {defaultValue: "nights"})}`
        },
        {
            key: "status",
            title: t("status", {defaultValue: "Status"}),
            sorter: textSorter(reservation => reservation.status),
            render: (_, reservation) => (
                <EnumTag value={reservation.status} tone={operationalRoomReservationStatusTone(reservation.status)}/>
            )
        }
    ], [t]);

    return (
        <DetailPanel
            title={t("accommodation", {defaultValue: "Accommodation"})}
            extra={(
                <Button
                    type="primary"
                    loading={pending.savingRoomReservation}
                    disabled={selectionDisabled}
                    onClick={() => forms.roomReservation.submit()}
                >
                    {t("save-accommodation", {defaultValue: "Save accommodation"})}
                </Button>
            )}
            description={!primaryRegistration ? t("participant-register-before-accommodation", {
                defaultValue: "Register the participant first so accommodation can be saved to the registration."
            }) : undefined}
        >
            {rooming.failed ? (
                <Alert
                    type="error"
                    showIcon
                    message={t("participant-accommodation-load-failed", {
                        defaultValue: "Accommodation could not be loaded. Refresh the page and try again."
                    })}
                />
            ) : null}
            <Form form={forms.roomReservation} onFinish={actions.saveRoomReservation}>
                <div style={FIELD_GRID_STYLE}>
                    <ParticipantFormField
                        label={roomChoiceLabel}
                        name="roomOptionKey"
                        rules={[{
                            required: true,
                            message: t("participant-room-reservation-missing-room", {
                                defaultValue: "Select a hotel and room type."
                            })
                        }]}
                    >
                        <Select
                            allowClear
                            aria-label={roomChoiceLabel}
                            loading={rooming.loading}
                            disabled={selectionDisabled}
                            notFoundContent={t("participant-accommodation-empty", {
                                defaultValue: "No accommodation periods are configured for this event."
                            })}
                            onChange={rooming.onOptionChange}
                            style={{width: "100%"}}
                            options={rooming.options.map(option => ({
                                value: option.key,
                                label: option.label
                            }))}
                        />
                    </ParticipantFormField>
                    <ParticipantFormField
                        label={arrivalLabel}
                        name="arrivalDate"
                        rules={[{
                            required: true,
                            message: t("participant-room-reservation-missing-dates", {
                                defaultValue: "Enter arrival and departure dates."
                            })
                        }]}
                    >
                        <Input aria-label={arrivalLabel} disabled={!primaryRegistration} type="date"/>
                    </ParticipantFormField>
                    <ParticipantFormField
                        label={departureLabel}
                        name="departureDate"
                        rules={[{
                            required: true,
                            message: t("participant-room-reservation-missing-dates", {
                                defaultValue: "Enter arrival and departure dates."
                            })
                        }]}
                    >
                        <Input aria-label={departureLabel} disabled={!primaryRegistration} type="date"/>
                    </ParticipantFormField>
                    <ParticipantFormField
                        label={roomCountLabel}
                        name="roomCount"
                        rules={[{
                            validator: (_, value) => toPositiveInt(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error(t("participant-room-reservation-missing-room-count", {
                                    defaultValue: "Enter the number of rooms."
                                })))
                        }]}
                    >
                        <Input aria-label={roomCountLabel} disabled={!primaryRegistration} min={1} type="number"/>
                    </ParticipantFormField>
                    <FieldTile label={t("hotel", {defaultValue: "Hotel"})}>
                        {displayValue(primaryRoomReservation?.hotelName)}
                    </FieldTile>
                    <FieldTile label={t("room-type", {defaultValue: "Room type"})}>
                        {displayValue(primaryRoomReservation?.roomTypeName)}
                    </FieldTile>
                    <FieldTile label={arrivalLabel}>
                        {formatLocalDate(primaryRoomReservation?.arrivalDate, EMPTY_OPERATIONS_VALUE)}
                    </FieldTile>
                    <FieldTile label={departureLabel}>
                        {formatLocalDate(primaryRoomReservation?.departureDate, EMPTY_OPERATIONS_VALUE)}
                    </FieldTile>
                </div>
            </Form>
            <FieldTile
                label={t("rooming", {defaultValue: "Rooming"})}
                detail={roomReservations.length > 0
                    ? t("room-reservation-count", {
                        count: roomReservations.length,
                        defaultValue: "{{count}} reservations"
                    })
                    : undefined}
            >
                {roomReservations.length > 0
                    ? <StatusTag tone="info">{t("reserved", {defaultValue: "Reserved"})}</StatusTag>
                    : <StatusTag>{t("no-reservation", {defaultValue: "None"})}</StatusTag>}
            </FieldTile>
            <Table
                size="small"
                rowKey="id"
                columns={reservationColumns}
                dataSource={roomReservations}
                pagination={false}
                scroll={{x: 760}}
                locale={{emptyText}}
            />
        </DetailPanel>
    );
};
